import React, { useRef, useState } from 'react';

const ALLOWED = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

function fileIcon(fileType) {
  if (fileType === 'application/pdf') return '📄';
  if (fileType?.startsWith('image/')) return '🖼️';
  return '📁';
}

function fileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function SourcesSidebar({
  documents,
  activeId,
  selectedIds,
  loadingDocs,
  uploading,
  uploadProgress,
  portfolioLoading,
  analyzingId,
  onOpen,
  onToggleSelect,
  onSelectAll,
  onClearAll,
  onAnalyze,
  onDelete,
  onUploadFile,
  onUploadFolder,
  onPortfolioAnalyze,
}) {
  const fileRef = useRef(null);
  const folderRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [folderFiles, setFolderFiles] = useState([]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const items = Array.from(e.dataTransfer.files || []).filter((f) => ALLOWED.includes(f.type));
    if (items.length) onUploadFile(items);
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => ALLOWED.includes(f.type));
    if (picked.length) onUploadFile(picked);
    e.target.value = '';
  };

  const handleFolderChange = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => ALLOWED.includes(f.type));
    setFolderFiles(picked);
    e.target.value = '';
  };

  const submitFolder = () => {
    if (folderFiles.length) {
      onUploadFolder(folderFiles);
      setFolderFiles([]);
    }
  };

  return (
    <aside className="nb-sources">
      <div className="nb-src-header">
        <div className="nb-src-title">📎 Sources</div>

        {/* Upload zone */}
        <div
          className={`upload-zone${dragging ? ' dragging' : ''}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <div className="upload-zone-icon">⬆️</div>
          <div className="upload-zone-text">
            <strong>Click or drag</strong> PDFs / images<br />
            supports multiple files
          </div>
          {uploading && uploadProgress > 0 && (
            <>
              <div className="upload-bar">
                <div className="upload-bar-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
              <div className="upload-progress-label">{uploadProgress}%</div>
            </>
          )}
        </div>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }} onChange={handleFileChange} />

        {/* Folder upload row */}
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <button
            className="sb-btn"
            style={{ flex: 1, fontSize: 11 }}
            onClick={() => folderRef.current?.click()}
            disabled={uploading}
          >
            📂 Folder
          </button>
          {folderFiles.length > 0 && (
            <button
              className="sb-btn primary"
              style={{ flex: 1, fontSize: 11 }}
              onClick={submitFolder}
              disabled={uploading}
            >
              Upload {folderFiles.length}
            </button>
          )}
        </div>
        <input
          ref={folderRef}
          type="file"
          multiple
          webkitdirectory=""
          directory=""
          style={{ display: 'none' }}
          onChange={handleFolderChange}
        />
      </div>

      <div className="nb-src-body">
        {loadingDocs && (
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <span className="spinner" />
          </div>
        )}

        {!loadingDocs && !documents.length && (
          <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--sb-muted)', fontSize: 12, lineHeight: 1.6 }}>
            No sources yet.<br />Upload a PDF or image to get started.
          </div>
        )}

        {selectedIds.length > 0 && (
          <div className="sel-bar">
            <span className="sel-bar-text">{selectedIds.length} selected</span>
            <button className="sel-clear" onClick={onClearAll}>Clear</button>
          </div>
        )}

        {documents.map((doc) => (
          <div
            key={doc._id}
            className={`src-card${activeId === doc._id ? ' active' : ''}`}
            onClick={() => onOpen(doc._id)}
          >
            <div className="src-card-top">
              <div className="src-icon">{fileIcon(doc.fileType)}</div>
              <div className="src-info">
                <div className="src-name" title={doc.originalName}>{doc.originalName}</div>
                <div className="src-meta">
                  {fileSize(doc.fileSize)} &middot;&nbsp;
                  <span className={`badge badge-${doc.analysis?.status || 'pending'}`}>
                    {doc.analysis?.status === 'processing' && <span className="spinner spinner-sm" />}
                    {doc.analysis?.status || 'pending'}
                  </span>
                </div>
              </div>
              <input
                type="checkbox"
                className="src-checkbox"
                checked={selectedIds.includes(doc._id)}
                onChange={(e) => { e.stopPropagation(); onToggleSelect(doc._id); }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className="src-actions" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => onAnalyze(doc._id)}
                disabled={!!analyzingId}
                style={{ fontSize: 10 }}
              >
                {analyzingId === doc._id ? <><span className="spinner spinner-sm" /> Analyzing…</> : '⚡ Analyze'}
              </button>
              <button
                className="btn btn-sm btn-danger-ghost"
                onClick={() => onDelete(doc._id)}
                style={{ fontSize: 10 }}
              >
                🗑
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="nb-src-footer">
        {documents.length > 0 && (
          <button
            className="sb-btn"
            onClick={selectedIds.length === documents.length ? onClearAll : onSelectAll}
            style={{ fontSize: 11 }}
          >
            {selectedIds.length === documents.length ? '☐ Deselect All' : '☑ Select All'}
          </button>
        )}
        <button
          className="sb-btn primary"
          onClick={onPortfolioAnalyze}
          disabled={portfolioLoading || selectedIds.length < 1}
        >
          {portfolioLoading
            ? <><span className="spinner spinner-sm" style={{ borderTopColor: 'white', borderColor: 'rgba(255,255,255,0.3)' }} /> Building…</>
            : `🔍 Analyze ${selectedIds.length > 0 ? `(${selectedIds.length})` : 'Selected'}`}
        </button>
      </div>
    </aside>
  );
}
